/**
 * Browser-side image variants for the media storage module.
 *
 * The monolith produced these with GD on upload; we produce them here with canvas and send the
 * finished files, so cms-api stores bytes and needs no image extension at all. The geometry must
 * match what the monolith did, because both write into the same table and the site requests the
 * same `_b` / `_s` / `_t` / `_f` suffixes:
 *
 *   Gumlet\ImageResize::resizeToBestFit($w, $h, allowEnlarge: true)
 *     -> scale = min($w / srcW, $h / srcH), applied whether it shrinks or enlarges,
 *        so the result touches the box on one side and never exceeds it.
 */

/**
 * Cap for the stored original. Anything larger is scaled down proportionally before upload —
 * a 4000x3000 phone photo becomes 1024x768 — and anything already inside the box is stored
 * byte-for-byte as it was picked, so small images are never re-encoded.
 */
export const ORIGINAL_MAX = [1024, 768];

/** Crop suffix => [maxWidth, maxHeight] — the monolith's sizes for an entity with no override. */
export const CROP_SIZES = {
  b: [450, 450],
  s: [280, 280],
  t: [180, 180],
  f: [1000, 800],
};

/**
 * Encoding quality for everything we produce. The monolith left GD on its default 85, which on
 * small crops shows as mush around edges; canvas artefacts are cheaper to avoid than to explain.
 */
const JPEG_QUALITY = 0.92;

/**
 * Decodes a File/Blob honouring EXIF orientation, so a phone photo is not stored sideways.
 * @returns {Promise<ImageBitmap|HTMLImageElement>}
 */
export const decodeImage = async (source) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(source, { imageOrientation: "from-image" });
    } catch (e) {
      // Older Safari: fall through to the <img> path, which applies orientation as well.
    }
  }

  const url = URL.createObjectURL(source);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not decode the image"));
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
};

export const dimensionsOf = (image) => ({
  width: image.width || image.naturalWidth,
  height: image.height || image.naturalHeight,
});

/**
 * Draws a region of `image` into a canvas of exactly width x height, halving in steps when
 * shrinking a lot. One-shot downscaling of a 4000px photo is what makes canvas output look
 * softer than GD's; halving keeps the detail a single drawImage throws away.
 */
export const drawRegion = (image, region, width, height) => {
  let canvas = document.createElement("canvas");
  let currentWidth = region.width;
  let currentHeight = region.height;

  canvas.width = currentWidth;
  canvas.height = currentHeight;
  const context = canvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    image,
    region.x, region.y, region.width, region.height,
    0, 0, currentWidth, currentHeight
  );

  while (currentWidth > width * 2 && currentHeight > height * 2) {
    const nextWidth = Math.max(width, Math.round(currentWidth / 2));
    const nextHeight = Math.max(height, Math.round(currentHeight / 2));
    const step = document.createElement("canvas");
    step.width = nextWidth;
    step.height = nextHeight;
    const stepContext = step.getContext("2d");
    stepContext.imageSmoothingEnabled = true;
    stepContext.imageSmoothingQuality = "high";
    stepContext.drawImage(canvas, 0, 0, currentWidth, currentHeight, 0, 0, nextWidth, nextHeight);
    canvas = step;
    currentWidth = nextWidth;
    currentHeight = nextHeight;
  }

  if (currentWidth === width && currentHeight === height) {
    return canvas;
  }

  const target = document.createElement("canvas");
  target.width = width;
  target.height = height;
  const targetContext = target.getContext("2d");
  targetContext.imageSmoothingEnabled = true;
  targetContext.imageSmoothingQuality = "high";
  targetContext.drawImage(canvas, 0, 0, currentWidth, currentHeight, 0, 0, width, height);

  return target;
};

export const toJpegBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the image"))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });

/** Centred region of `width` x `height` inside a source of `sourceWidth` x `sourceHeight`. */
export const centredRegion = (sourceWidth, sourceHeight, width, height) => ({
  x: Math.max(0, Math.round((sourceWidth - width) / 2)),
  y: Math.max(0, Math.round((sourceHeight - height) / 2)),
  width: Math.min(sourceWidth, width),
  height: Math.min(sourceHeight, height),
});

/**
 * Cover: the largest centred region with the box's aspect ratio, scaled down to the box.
 *
 * This is what the monolith was reaching for and mostly got right, except for two cases its
 * implementation gets wrong and this one does not:
 *
 *  - it cropped the source to the `b` box's ratio first (a square, for these sizes) and then
 *    called Gumlet's `resize($w, $h)`, which does not preserve the ratio — so `_f` of a big
 *    photo came out as a square squashed into 1000x800, visibly stretched;
 *  - that same intermediate threw away image it did not have to: a 1200x798 source produced
 *    a 798x638 `_f` instead of the 997x798 the source could give.
 *
 * b/s/t come out identical to the monolith either way — they are squares of a square crop.
 *
 * Nothing is ever enlarged: a source smaller than the box yields the box's ratio at native
 * pixels (a blurry upscale is worse than an honestly smaller image).
 *
 * @returns {{region: {x: number, y: number, width: number, height: number}, width: number, height: number}}
 */
export const planSize = (source, [boxWidth, boxHeight]) => {
  const region = centredRegion(
    source.width,
    source.height,
    Math.min(source.width, Math.round((source.height * boxWidth) / boxHeight)),
    Math.min(source.height, Math.round((source.width * boxHeight) / boxWidth))
  );

  const fits = region.width >= boxWidth && region.height >= boxHeight;

  return {
    region,
    width: fits ? boxWidth : region.width,
    height: fits ? boxHeight : region.height,
  };
};

/**
 * The size of every crop for a given source, without touching any pixels — what the geometry
 * above will produce. Exists so the result can be asserted against real images.
 *
 * @returns {Record<string, {width: number, height: number}>}
 */
export const planCrops = (sourceWidth, sourceHeight) => {
  const source = { width: sourceWidth, height: sourceHeight };
  const plan = {};

  for (const [suffix, box] of Object.entries(CROP_SIZES)) {
    const { width, height } = planSize(source, box);
    plan[suffix] = { width, height };
  }

  return plan;
};

/** Box-fit size, enlarging when the source is smaller — resizeToBestFit(..., allowEnlarge: true). */
export const bestFitSize = (sourceWidth, sourceHeight, maxWidth, maxHeight) => {
  const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);

  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
};

/**
 * One crop per entry of CROP_SIZES, as JPEG blobs, with the monolith's geometry.
 * @param {ImageBitmap|HTMLImageElement|HTMLCanvasElement} image decoded source, or a crop canvas
 * @returns {Promise<Record<string, Blob>>}
 */
export const buildCrops = async (image) => {
  const source = dimensionsOf(image);
  const crops = {};

  for (const [suffix, box] of Object.entries(CROP_SIZES)) {
    const { region, width, height } = planSize(source, box);
    // eslint-disable-next-line no-await-in-loop
    crops[suffix] = await toJpegBlob(drawRegion(image, region, width, height));
  }

  return crops;
};

/**
 * Everything cms-api needs for one upload: the original (capped at ORIGINAL_MAX) and the crops.
 *
 * The crops are always derived from the full-resolution decode, not from the capped original —
 * scaling once from the source is better than scaling twice, and it costs nothing here.
 */
export const buildVariants = async (file) => {
  const image = await decodeImage(file);
  const crops = await buildCrops(image);
  const original = await buildOriginal(image, file);

  if (typeof image.close === "function") {
    image.close();
  }

  return { original, ...crops };
};

/**
 * The original as it will be stored: scaled into ORIGINAL_MAX when it is bigger, otherwise the
 * picked file itself (re-encoding an image that already fits would only cost quality).
 */
export const buildOriginal = async (image, file) => {
  const [maxWidth, maxHeight] = ORIGINAL_MAX;
  const source = dimensionsOf(image);

  if (source.width <= maxWidth && source.height <= maxHeight) {
    return file;
  }

  const { width, height } = bestFitSize(source.width, source.height, maxWidth, maxHeight);
  const whole = { x: 0, y: 0, width: source.width, height: source.height };

  return toJpegBlob(drawRegion(image, whole, width, height));
};
